import axios from 'axios';
import { PaymentResponseCode } from 'config/constants/order.constants';
import { AuthTranscription, GeneralTranscription, OrderTranscription, UserTranscription } from 'config/constants/transcription.constant';

export const authErrors = ['JWT_EXPIRED', 'JWT_NOT_ACEPTED', 'UNAUTHORIZED'];

const paymentNotesMap = {
  [PaymentResponseCode.TRANSACTION_APPROVED]: 'TRANSACTION_APPROVED',
  [PaymentResponseCode.TRANSACTION_DATA_ENTRY_ERROR]: 'TRANSACTION_DATA_ENTRY_ERROR',
  [PaymentResponseCode.ERROR_PROCCESSING_TRANSACTION]: 'ERROR_PROCCESSING_TRANSACTION',
  [PaymentResponseCode.ERROR_IN_TRANSACTION]: 'ERROR_IN_TRANSACTION',
  [PaymentResponseCode.REJECTED_BY_SENDER]: 'REJECTED_BY_SENDER',
  [PaymentResponseCode.POSIBLE_FRAUD]: 'POSIBLE_FRAUD',
};

export type TypeWithKey<T> = {
  [key: string]: T;
};

export const errorTypeCode: TypeWithKey<string> = {
  ERR_NETWORK: GeneralTranscription.ERROR_NETWORK,
  USER_NOT_FOUND: UserTranscription.ERROR_USER_NOT_FOUND,
  INVALID_CREDENTIALS: AuthTranscription.ERROR_INVALID_CREDENTIALS,
  JWT_EXPIRED: AuthTranscription.ERROR_JWT_EXPIRED,
  JWT_NOT_ACEPTED: AuthTranscription.ERROR_JWT_NOT_ACEPTED,
  UNAUTHORIZED: AuthTranscription.ERROR_UNAUTHORIZED,
  TOO_MANY_REQUESTS: GeneralTranscription.ERROR_TOO_MANY_REQUESTS,
  PLEASE_WAIT_3_MINS_TO_GET_ANOTHER_RECOVER_PASSWORD_MAIL: UserTranscription.ERROR_PLEASE_WAIT_3_MINS_TO_GET_ANOTHER_RECOVER_PASSWORD_MAIL,
  USER_HAS_MAX_FAVORITES_ADDED: UserTranscription.ERROR_USER_HAS_MAX_FAVORITES_ADDED,
  INVALID_DNI: UserTranscription.ERROR_INVALID_DNI,
  IDEMPOTENCY_KEY_INVALID: OrderTranscription.ERROR_IDEMPOTENCY_KEY_INVALID,
  IDEMPOTENCY_CONFLICT: OrderTranscription.ERROR_IDEMPOTENCY_CONFLICT,
  IDEMPOTENCY_IN_PROGRESS: OrderTranscription.ERROR_IDEMPOTENCY_IN_PROGRESS,
  BAD_LIFECYCLE_REQUEST_ERROR: OrderTranscription.ERROR_BAD_LIFECYCLE_REQUEST,
  ORDER_ALREADY_PAID: OrderTranscription.ERROR_ORDER_ALREADY_PAID,
  AMOUNT_MISMATCH: OrderTranscription.ERROR_AMOUNT_MISMATCH,
};

export type ApiError = {
  code?: string;
  message?: string;
  status?: number;
  isNetwork: boolean;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value : undefined);

export const toApiError = (error: unknown): ApiError => {
  if (!axios.isAxiosError<unknown>(error)) {
    return {
      message: error instanceof Error ? error.message : undefined,
      isNetwork: false,
    };
  }

  const responseData = isRecord(error.response?.data) ? error.response.data : undefined;
  const responseError = isRecord(responseData?.error) ? responseData.error : undefined;
  const responseCode = getString(responseError?.code) ?? getString(responseData?.code);
  const isNetwork = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';

  return {
    code: responseCode ?? (isNetwork ? 'ERR_NETWORK' : getString(error.code)),
    message:
      getString(responseError?.message) ?? getString(responseData?.message) ?? (!error.response ? getString(error.message) : undefined),
    status: error.response?.status,
    isNetwork,
  };
};

export const getValidationError = (errorCode?: string): string | undefined => {
  return errorCode ? errorTypeCode[errorCode] : undefined;
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = toApiError(error);
  if (apiError.isNetwork) return errorTypeCode.ERR_NETWORK;
  return getValidationError(apiError.code) ?? apiError.message ?? fallback;
};

export const getPaymentNotesfromResponseCode = (code: number): string => {
  return paymentNotesMap[code] || 'UNKNOWN_AUTHENTICATION_STATUS';
};

export const getAuthErrorCode = (error: unknown): string | undefined => {
  return toApiError(error).code;
};

export const isAuthError = (error: unknown): boolean => {
  const code = getAuthErrorCode(error);
  return Boolean(code && authErrors.includes(code));
};
